// Premium Animation Themes for Living Announcements
export type AnimationTheme = 
  | 'elegant_floral'
  | 'golden_light'
  | 'blooming_garden'
  | 'handwritten_journal'
  | 'ornate_keepsake'
  | 'memory_constellation';

export interface AnimationThemeConfig {
  value: AnimationTheme;
  label: string;
  description: string;
  closedCardStyle: string;
  openingAnimation: 'envelope' | 'unfold' | 'light_reveal' | 'bloom' | 'frame_reveal';
  accentColor: string;
  bgGradient: string;
  borderStyle: string;
  particleType: 'petals' | 'sparkles' | 'leaves' | 'ink' | 'gold_dust';
}

export const ANIMATION_THEMES: AnimationThemeConfig[] = [
  {
    value: 'elegant_floral',
    label: 'Elegant Floral Letter',
    description: 'A delicate floral envelope that blooms open to reveal your message.',
    closedCardStyle: 'from-rose-50 via-pink-50 to-amber-50',
    openingAnimation: 'envelope',
    accentColor: 'rose',
    bgGradient: 'from-rose-50/90 via-pink-50/80 to-white',
    borderStyle: 'border-rose-200/60',
    particleType: 'petals',
  },
  {
    value: 'golden_light',
    label: 'Golden Light Reveal',
    description: 'A luminous golden seal breaks open with radiant light.',
    closedCardStyle: 'from-amber-100 via-yellow-50 to-orange-50',
    openingAnimation: 'light_reveal',
    accentColor: 'amber',
    bgGradient: 'from-amber-50/90 via-yellow-50/80 to-white',
    borderStyle: 'border-amber-300/60',
    particleType: 'sparkles',
  },
  {
    value: 'blooming_garden',
    label: 'Blooming Garden Frame',
    description: 'Botanical vines and flowers grow to frame your message.',
    closedCardStyle: 'from-emerald-50 via-green-50 to-teal-50',
    openingAnimation: 'bloom',
    accentColor: 'emerald',
    bgGradient: 'from-emerald-50/90 via-green-50/80 to-white',
    borderStyle: 'border-emerald-200/60',
    particleType: 'leaves',
  },
  {
    value: 'handwritten_journal',
    label: 'Handwritten Journal',
    description: 'A leather journal opens to reveal your handwritten words.',
    closedCardStyle: 'from-stone-100 via-amber-50 to-orange-50/80',
    openingAnimation: 'unfold',
    accentColor: 'stone',
    bgGradient: 'from-stone-50/90 via-amber-50/80 to-white',
    borderStyle: 'border-stone-300/60',
    particleType: 'ink',
  },
  {
    value: 'ornate_keepsake',
    label: 'Ornate Keepsake Frame',
    description: 'An ornate gilded frame reveals your cherished message.',
    closedCardStyle: 'from-violet-50 via-purple-50 to-indigo-50',
    openingAnimation: 'frame_reveal',
    accentColor: 'violet',
    bgGradient: 'from-violet-50/90 via-purple-50/80 to-white',
    borderStyle: 'border-violet-200/60',
    particleType: 'gold_dust',
  },
  {
    value: 'memory_constellation',
    label: 'Memory Constellation',
    description: 'Your message forms star-by-star in a magical night sky constellation.',
    closedCardStyle: 'from-indigo-950 via-blue-950 to-slate-950',
    openingAnimation: 'light_reveal',
    accentColor: 'indigo',
    bgGradient: 'from-indigo-950/90 via-blue-950/80 to-slate-950',
    borderStyle: 'border-indigo-400/40',
    particleType: 'sparkles',
  },
];

export function getAnimationTheme(theme: string): AnimationThemeConfig {
  return ANIMATION_THEMES.find(t => t.value === theme) || ANIMATION_THEMES[0];
}
